import { useState, useEffect, useCallback } from 'react';

export type A11yTheme = 'white' | 'black' | 'blue';
export type A11yFont = 'normal' | 'large' | 'xlarge';
export type A11yImages = 'on' | 'off';
export type A11yLetterSpacing = 'normal' | 'large';

export interface A11yState {
    enabled: boolean;
    theme: A11yTheme;
    font: A11yFont;
    images: A11yImages;
    letterSpacing: A11yLetterSpacing;
}

const STORAGE_KEY = 'a11y-settings';
const FONT_ORDER: A11yFont[] = ['normal', 'large', 'xlarge'];

const DEFAULT_STATE: A11yState = {
    enabled: false,
    theme: 'white',
    font: 'normal',
    images: 'on',
    letterSpacing: 'normal',
};

const readStored = (): A11yState => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return DEFAULT_STATE;
        return { ...DEFAULT_STATE, ...JSON.parse(raw) };
    } catch {
        return DEFAULT_STATE;
    }
};

export const useAccessibility = () => {
    const [state, setState] = useState<A11yState>(readStored);

    useEffect(() => {
        const html = document.documentElement;
        if (state.enabled) {
            html.setAttribute('data-a11y', 'true');
            html.setAttribute('data-a11y-theme', state.theme);
            html.setAttribute('data-a11y-font', state.font);
            html.setAttribute('data-a11y-images', state.images);
            html.setAttribute('data-a11y-ls', state.letterSpacing);
        } else {
            html.removeAttribute('data-a11y');
            html.removeAttribute('data-a11y-theme');
            html.removeAttribute('data-a11y-font');
            html.removeAttribute('data-a11y-images');
            html.removeAttribute('data-a11y-ls');
        }
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
            /* localStorage недоступен — не критично */
        }
    }, [state]);

    const patch = useCallback(
        (nextState: Partial<A11yState>) => setState((current) => ({ ...current, ...nextState })),
        [],
    );

    const toggle = useCallback(() => setState((current) => ({ ...current, enabled: !current.enabled })), []);
    const disable = useCallback(() => setState((current) => ({ ...current, enabled: false })), []);
    const setTheme = useCallback((theme: A11yTheme) => patch({ theme }), [patch]);
    const setImages = useCallback((images: A11yImages) => patch({ images }), [patch]);
    const setLetterSpacing = useCallback(
        (letterSpacing: A11yLetterSpacing) => patch({ letterSpacing }),
        [patch],
    );

    const incFont = useCallback(
        () =>
            setState((current) => {
                const index = FONT_ORDER.indexOf(current.font);
                return { ...current, font: FONT_ORDER[Math.min(index + 1, FONT_ORDER.length - 1)] };
            }),
        [],
    );
    const decFont = useCallback(
        () =>
            setState((current) => {
                const index = FONT_ORDER.indexOf(current.font);
                return { ...current, font: FONT_ORDER[Math.max(index - 1, 0)] };
            }),
        [],
    );

    return {
        ...state,
        toggle,
        disable,
        setTheme,
        setImages,
        setLetterSpacing,
        incFont,
        decFont,
    };
};
